import React, { useState, useContext, useMemo } from "react";
import {
  Row,
  Button,
  Grid,
  AuthContext,
  ViewContext,
  Loader,
  useAPI,
} from "components/lib";
import { useNavigate } from "react-router-dom";
import MaterialReactTable from "material-react-table";
import {
  groupStatusBadge,
  isV2Group,
  isAwaitingApproval,
  filterAwaitingApproval,
} from "./client-group-v2-display";

export function ClientGroupsView() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("");
  // R21: the v2 groups whose XPM sync waits for staff. Filtered here, on the
  // metadata the list already returns, so the list endpoint does not change.
  const [awaitingOnly, setAwaitingOnly] = useState(false);
  const groupsAPI = useAPI(
    `/api/client-groups${statusFilter ? `?status=${statusFilter}` : ""}`
  );

  const columns = useMemo(
    () => [
      { accessorKey: "name", header: "Group Name", size: 200 },
      { accessorKey: "entity_count", header: "Entities", size: 80 },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ cell, row }) => {
          const badge = groupStatusBadge(cell.getValue());
          return (
            <>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}
              >
                {badge.text}
              </span>
              {isAwaitingApproval(row.original) && (
                <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-sg-orange-bg text-sg-orange whitespace-nowrap">
                  Awaiting approval
                </span>
              )}
            </>
          );
        },
      },
      {
        accessorKey: "metadata",
        header: "Submitted By",
        size: 200,
        Cell: ({ cell }) => {
          const metadata =
            typeof cell.getValue() === "string"
              ? JSON.parse(cell.getValue())
              : cell.getValue();
          return metadata?.submitted_by_email || "-";
        },
      },
      {
        accessorKey: "created_at",
        header: "Date",
        size: 150,
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return date ? new Date(date).toLocaleDateString() : "-";
        },
      },
    ],
    []
  );

  const groupsData = groupsAPI?.data;
  const groups = useMemo(() => groupsData || [], [groupsData]);
  const tableData = useMemo(
    () => (awaitingOnly ? filterAwaitingApproval(groups) : groups),
    [awaitingOnly, groups]
  );

  if (groupsAPI.loading) return <Loader />;

  // An account with no v2 group sees the list as it always has.
  const showAwaitingFilter = awaitingOnly || groups.some(isV2Group);

  return (
    <Grid>
      <Row>
        <h1 className="text-2xl font-bold">Client Groups</h1>
        <div className="flex gap-2 ml-auto">
          {["", "submitted", "in_review", "complete", "archived"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded-full text-sm ${
                statusFilter === s
                  ? "bg-sg-blue text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {s || "All"}
            </button>
          ))}
          {showAwaitingFilter && (
            <button
              onClick={() => setAwaitingOnly((on) => !on)}
              className={`px-3 py-1 rounded-full text-sm ${
                awaitingOnly
                  ? "bg-sg-blue text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Awaiting approval
            </button>
          )}
        </div>
      </Row>

      <MaterialReactTable
        columns={columns}
        data={tableData}
        enableColumnActions={false}
        enableColumnFilters={false}
        enablePagination={true}
        enableSorting={true}
        enableBottomToolbar={true}
        enableTopToolbar={false}
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => navigate(`/client-groups/${row.original.id}`),
          sx: { cursor: "pointer" },
        })}
      />
    </Grid>
  );
}

export default ClientGroupsView;
